<script setup lang="ts">
import type { PaymentCard } from '@/types/common';
import type { PaymentMethod } from '@stripe/stripe-js';

const emit = defineEmits<{
	(e: 'onClose'): void
	(e: 'onBack'): void
	(e: 'onPaymentFail'): void
	(e: 'onPaymentSuccess'): void
	(e: 'onOrderStatusFail'): void
	(e: 'onPlaceOrderPayment', method: string): void
}>();

const props = defineProps({
	eventId: {
		type: Number as PropType<number | null>,
		required: true,
	},
	orderId: {
		type: String,
		default: '',
	},
	eventName: {
		type: String,
		required: true,
	},
	eventCountry: {
		type: Number as PropType<number | null>,
		default: null,
		required: true,
	},
	isOpen: {
		type: Boolean,
		default: false,
	},
	isPaidToken: {
		type: Boolean,
		default: false,
	},
});

const route = useRoute();
const eventStore = useEventStore();
const checkoutStore = useCheckoutStore();
const profileStore = useProfileStore();
const notificationStore = useNotificationStore();
const attendeeStore = useAttendeeStore();

const selectedPaymentOption = ref<string>();
const isFollowOrganizer = ref(true);
const isSaveCard = ref(true);
const creditCardFormRef = ref();
const isEnablePolling = ref(false);
const isPaymentProcessing = ref(false);

const isPaidOrder = computed(() => {
	return checkoutStore.orderSummary.total > 0;
});

const pageTitle = computed(() => {
	return isPaidOrder.value ? 'Payment details' : 'Order details';
});

const processingMessage = computed(() => {
	return isPaidOrder.value ? 'Your payment is processing' : 'Your order is processing';
});

const isUSEvent = computed(() => eventStore.isUSEvent);
const isLoading = computed(() => checkoutStore.userCheckoutOptionsResponse.isLoading);
const createdOrderId = computed(() => checkoutStore.tempOrderId);
const eventId = computed(() => props.eventId || null);
const isAlreadyFollowOrganizer = computed(() => checkoutStore.userCheckoutOptions?.isFollowing || false);
const savedPaymentCards = computed(() => checkoutStore.userCheckoutOptions?.paymentCards || []);
const isSidebarOpen = computed(() => props.isOpen);
const totalAmount = computed(() => checkoutStore.orderSummary.total);
const checkoutOrderTotalAmount = computed(() => checkoutStore.checkoutOrdersData?.totalAmount || 1);
const eventName = computed(() => props.eventName);
const computedOrderId = computed(() => createdOrderId.value || props.orderId);
const currentFullUrl = computed(() => {
	// Accessing the full path of the current route
	return getFullUrlPath(route.fullPath);
});

const {
	isApplePayAvailable,
	isGooglePayAvailable,
	paymentRequestInstance,
	stripeCreditCardInstance,
	stripeWalletInstance,
	isVerifyingAvailablePaymentOption,
} = usePaymentRequest(eventName, totalAmount, isSidebarOpen);

async function handlePollingCallbackSuccess() {
	// Fetch for order details after successfully polling an order status
	if (checkoutStore.salesId && eventId.value) {
		await attendeeStore.getOrder(eventId.value, checkoutStore.salesId);
	}
}

const { isProcessing, orderStatus } = usePollingOrderStatus(
	computedOrderId,
	isEnablePolling,
	handlePollingCallbackSuccess,
);

function fetchCheckOrderStatus() {
	isEnablePolling.value = true;
}

function resetInternalState() {
	selectedPaymentOption.value = '';
	isFollowOrganizer.value = false;
	isSaveCard.value = true;
	creditCardFormRef.value = null;
	isEnablePolling.value = false;
	isPaymentProcessing.value = false;
}

async function handleCreatePaymentMethod() {
	// Create payment method
	const res = await creditCardFormRef.value.createStripePaymentMethod();

	if (res?.error?.decline_code === 'card_not_supported') {
		notificationStore.showWarningNotification('This card is not supported.');
	}

	const paymentMethod: PaymentMethod = res.paymentMethod;
	const isCardNameValid: boolean = res.isCardNameValid;
	const savedCardInfo: PaymentCard = res.savedCardInfo;

	return {
		paymentMethod,
		isCardNameValid,
		savedCardInfo,
	};
}

async function handleUpdateBuyerInfo() {
	if (!computedOrderId.value) {
		return false;
	}

	const payload = generateBuyerInfo(profileStore.profile, isFollowOrganizer.value, isSaveCard.value);

	await checkoutStore.updateCheckoutOrderBuyerInfoByOrderId(computedOrderId.value, payload);

	if (checkoutStore.updateCheckoutOrderResponse.errorMessage) {
		notificationStore.showErrorNotification(checkoutStore.updateCheckoutOrderResponse.errorMessage);
		return false;
	}
	return true;
}

async function handleConfirmCheckoutOrder(paymentId?: string) {
	if (!computedOrderId.value) {
		return false;
	}

	await checkoutStore.confirmCheckoutOrdersByOrderId({
		orderId: computedOrderId.value,
		paymentMethodId: paymentId,
	});
	if (checkoutStore.confirmCheckoutOrderResponse.errorMessage) {
		notificationStore.showErrorNotification("Something went wrong.", "Please try again or try with a different payment method.");
		console.error(checkoutStore.confirmCheckoutOrderResponse.errorMessage);
		return false;
	}
	return true;
}

async function handleConfirmStripePayment() {
	let paymentResponse = null;
	try {
		paymentResponse = await creditCardFormRef.value.confirmStripePayment(checkoutStore.paymentSecret);
	} catch {
		emit('onPaymentFail');
		return false;
	}

	if (paymentResponse?.error) {
		emit('onPaymentFail');
		return false;
	}
	return true;
}

async function handlePayWithCard() {
	// Step 1: Create payment method
	const { paymentMethod, isCardNameValid, savedCardInfo } = await handleCreatePaymentMethod();
	let paymentId;
	if (savedCardInfo) {
		// Use save card info
		paymentId = savedCardInfo.stripePaymentMethodId;
	} else if (paymentMethod && isCardNameValid) {
		// Use new card info
		paymentId = paymentMethod.id;
	} else {
		// Create payment method error.
		return;
	}

	isPaymentProcessing.value = true;

	// Step 2: Update buyer info
	const isUpdateBuyerInfoSuccess = await handleUpdateBuyerInfo();
	if (!isUpdateBuyerInfoSuccess) {
		isPaymentProcessing.value = false;
		return;
	}

	// Step 3: Confirm checkout order
	const isConfirmCheckoutSuccess = await handleConfirmCheckoutOrder(paymentId);
	if (!isConfirmCheckoutSuccess) {
		isPaymentProcessing.value = false;
		return;
	}

	// Step 4: Confirm payment with stripe
	const isConfirmStripePaymentSuccess = await handleConfirmStripePayment();
	if (!isConfirmStripePaymentSuccess) {
		isPaymentProcessing.value = false;
		return;
	}
	fetchCheckOrderStatus();
}

async function handlePayWithWallet() {
	if (!paymentRequestInstance.value) {
		return;
	}

	// Trigger shows the browser’s payment interface
	paymentRequestInstance.value.show();

	// Handle event listener when user select payment from google pay
	paymentRequestInstance.value.on('paymentmethod', async (ev: any) => {
		isPaymentProcessing.value = true;
		const paymentId = ev.paymentMethod.id;
		// Step 1: Update buyer info
		const isUpdateBuyerInfoSuccess = await handleUpdateBuyerInfo();
		if (!isUpdateBuyerInfoSuccess) {
			return;
		}

		// Step 2: Confirm checkout order
		const isConfirmCheckoutSuccess = await handleConfirmCheckoutOrder(paymentId);
		if (!isConfirmCheckoutSuccess) {
			ev.complete('success'); // Close wallet payment interface
			isPaymentProcessing.value = false;
			emit('onPaymentFail');
			return;
		}

		if (!stripeCreditCardInstance.value) {
			isPaymentProcessing.value = false;
			return;
		}

		const { error: confirmError } = await stripeCreditCardInstance.value.confirmPayment({
			clientSecret: checkoutStore.paymentSecret,
			confirmParams: {
				return_url: currentFullUrl.value,
			},
			redirect: 'if_required',
		});

		if (confirmError) {
			isPaymentProcessing.value = false;
			emit('onPaymentFail');
		} else {
			fetchCheckOrderStatus();
		}

		ev.complete('success'); // Close wallet payment interface
	});
}

async function handlePlaceOrder() {
	if (isPaidOrder.value) {
		if (!selectedPaymentOption.value) {
			return;
		}

		switch (selectedPaymentOption.value) {
			case PAYMENT_METHOD.CREDIT_CARD:
				if (props.eventCountry === COUNTRY_CODE.UNITED_STATES) {
					emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.US_CREDIT_CARD);
				} else if (props.eventCountry === COUNTRY_CODE.JAPAN) {
					emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.JP_CREDIT_CARD);
				} else if (props.eventCountry === COUNTRY_CODE.SINGAPORE) {
					emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.SG_CREDIT_CARD);
				}
				handlePayWithCard();
				break;
			case PAYMENT_METHOD.GOOGLE_PAY:
				emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.GOOGLE_PAY);
				handlePayWithWallet();
				break;
			case PAYMENT_METHOD.APPLE_PAY:
				emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.APPLE_PAY);
				handlePayWithWallet();
				break;
			default:
				break;
		}
	} else {
		emit('onPlaceOrderPayment', GTM_PAYMENT_METHOD_PARAMS.FREE);
		isPaymentProcessing.value = true;

		// Step 1: Update buyer info
		const isUpdateBuyerInfoSuccess = await handleUpdateBuyerInfo();
		if (!isUpdateBuyerInfoSuccess) {
			return;
		}

		// Step 2: Confirm checkout order
		const isConfirmCheckoutSuccess = await handleConfirmCheckoutOrder();
		if (!isConfirmCheckoutSuccess) {
			return;
		}
		emit('onPaymentSuccess');
	}
}

function handleSaveCardChange(isSave: boolean) {
	isSaveCard.value = isSave;
}

watch(isProcessing, (newIsProcessing) => {
	isPaymentProcessing.value = newIsProcessing;
});

watch(orderStatus, (newOrderStatus) => {
	if (newOrderStatus === POLLING_ORDER_STATUS.SUCCESS) {
		emit('onPaymentSuccess');
	} else if (newOrderStatus === POLLING_ORDER_STATUS.FAIL) {
		emit('onOrderStatusFail');
	}
});

watch(() => props.isOpen, (isOpen) => {
	if (isOpen && profileStore.profile?.id && props.eventId) {
		checkoutStore.getUserCheckoutOptionsById(profileStore.profile.id, props.eventId);
	} else if (!isOpen) {
		resetInternalState();
	}
});
</script>

<template>
  <BaseSideDrawer
    :class="{ 'drawer-full-content': isPaymentProcessing }"
    :is-open="isOpen"
    :is-closable="!isPaymentProcessing"
    :persistent="isPaymentProcessing || isPaidToken"
    @on-close="emit('onClose')"
  >
    <template
      v-if="!isPaymentProcessing"
      #header
    >
      <div
        class="form-header"
      >
        <DotPointsIcon />
        <h1 class="text-xl text-semibold">
          {{ pageTitle }}
        </h1>
      </div>
    </template>
    <template #content>
      <div :class="['form-content', { 'hide': isPaymentProcessing }]">
        <template v-if="isPaidOrder">
          <div class="payment-selection-container">
            <label class="text-sm text-medium">Pay with</label>
            <template v-if="isLoading">
              <ButtonSkeletonLoader
                v-for="count in 3"
                :key="count"
                class="action-btn"
                height="58"
              />
            </template>
            <template v-else>
              <BaseRadioButton
                id="pay-with-card"
                v-model="selectedPaymentOption"
                :name="PAYMENT_METHOD.CREDIT_CARD"
                :value="PAYMENT_METHOD.CREDIT_CARD"
              >
                <template #icon>
                  <CreditCardIcon
                    class="icon-payment credit-card"
                    :color="ICON_COLOR.DEFAULT"
                  />
                </template>
                <template #label>
                  <span>Credit or debit</span>
                </template>
              </BaseRadioButton>
              <CreditCardForm
                v-if="selectedPaymentOption === PAYMENT_METHOD.CREDIT_CARD"
                ref="creditCardFormRef"
                :cards="savedPaymentCards"
                :total-amount="checkoutOrderTotalAmount"
                :loading="checkoutStore.userCheckoutOptionsResponse.isLoading"
                :stripe-instance="stripeWalletInstance"
                :stripe-instance-wt-account-id="stripeCreditCardInstance"
                @on-save-card-change="handleSaveCardChange"
              />
              <template v-if="isUSEvent">
                <div
                  v-if="isVerifyingAvailablePaymentOption"
                  class="other-options"
                >
                  <VSkeletonLoader type="list-item" />
                  <VSkeletonLoader type="list-item" />
                </div>
                <template v-else>
                  <BaseRadioButton
                    v-if="isApplePayAvailable"
                    id="pay-with-apple"
                    v-model="selectedPaymentOption"
                    :name="PAYMENT_METHOD.APPLE_PAY"
                    :value="PAYMENT_METHOD.APPLE_PAY"
                  >
                    <template #icon>
                      <ApplePayIcon class="icon-payment wallet" />
                    </template>
                    <template #label>
                      <span>Apple Pay</span>
                    </template>
                  </BaseRadioButton>
                  <BaseRadioButton
                    v-if="isGooglePayAvailable"
                    id="pay-with-google-pay"
                    v-model="selectedPaymentOption"
                    :name="PAYMENT_METHOD.GOOGLE_PAY"
                    :value="PAYMENT_METHOD.GOOGLE_PAY"
                  >
                    <template #icon>
                      <GooglePayIcon class="icon-payment wallet" />
                    </template>
                    <template #label>
                      <span>Google Pay</span>
                    </template>
                  </BaseRadioButton>
                </template>
              </template>
            </template>
          </div>
          <Divider />
        </template>
        <div class="user-info">
          <SentencesSkeletonLoader v-if="isLoading" />
          <template v-else>
            <h2 class="text-sm text-medium">
              Account information
            </h2>
            <span class="text-sm text-regular">{{ profileStore.profile?.email }}</span>
          </template>
        </div>
        <Divider />
        <div
          v-if="isLoading"
          class="order-summary-loader"
        >
          <SentencesSkeletonLoader />
          <div class="informed-loader">
            <SentencesSkeletonLoader />
          </div>
        </div>
        <template v-else>
          <OrderSummaryItems :data="checkoutStore.orderSummary" />
          <template
            v-if="!isAlreadyFollowOrganizer"
          >
            <Divider />
            <div class="follow-organizer-container">
              <BaseAlert
                :type="ALERT_TYPE.PRIMARY"
                hide-icon
              >
                <template #icon>
                  <AnnouncementIcon />
                </template>
                <span class="text-sm text-semibold">
                  Stay informed
                </span>
                <BaseCheckbox
                  id="follow-organizer"
                  v-model="isFollowOrganizer"
                  class="checkbox"
                  label="Keep me updated about this organizer’s events."
                />
              </BaseAlert>
            </div>
          </template>
        </template>
      </div>
      <OrderProcessing v-if="isPaymentProcessing">
        {{ processingMessage }}
      </OrderProcessing>
    </template>
    <template
      v-if="!isPaymentProcessing"
      #footer
    >
      <div class="form-footer">
        <template v-if="isLoading">
          <ButtonSkeletonLoader
            height="42"
            width="68"
          />
          <ButtonSkeletonLoader
            height="42"
            width="110"
          />
        </template>
        <template v-else>
          <BaseButton
            v-if="!isPaidOrder"
            size="md"
            color="gray"
            variant="outlined"
            @click="emit('onBack')"
          >
            Back
          </BaseButton>
          <BaseButton
            size="md"
            @click="handlePlaceOrder"
          >
            Place order
          </BaseButton>
        </template>
      </div>
    </template>
  </BaseSideDrawer>
</template>

<style scoped lang="scss">
.drawer-full-content {
  :deep(.drawer-content.--extend) {
    height: 100%;
  }
}

.form {
  &-header {
    display: flex;
    align-items: center;
    gap: spacings-get(2)
  }

  &-content {
    display: flex;
    flex-direction: column;
    gap: spacings-get(6);

    .icon-payment {
      background: colors-get(base, white);
      border: 1px solid colors-get(gray, 100);
      box-sizing: content-box;
      border-radius: 4px;

      &.credit-card {
        padding: rem(6) rem(9);
      }

      &.wallet {
        width: rem(36);
        height: rem(30);
        padding: 0 rem(4);
      }
    }
  }

  &-footer {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: spacings-get(3);
    height: 100%;
    padding: 0 spacings-get(6);
    color: colors-get(base, white);
    border-top: 1px solid #{colors-get(gray, 200)};
  }
}

.hide {
  display: none !important;
}

.payment-selection-container {
  display: flex;
  flex-direction: column;
  gap: spacings-get(3);
}

.follow-organizer-container {
  .checkbox {
    width: max-content;
  }
  label {
    color: colors-get(gray, 600);
  }
}

:deep(.--discount) {
  color: colors-get(error, 500);
}

.other-options {
  display: flex;
  flex-direction: column;
  row-gap: spacings-get(3);
  margin-top: spacings-get(4);
}

.order-summary-loader {
  display: flex;
  flex-direction: column;
  row-gap: spacings-get(6);

  .informed-loader {
    padding: spacings-get(3);
    background-color: colors-get(gray, 50);
    @include border-radius-default;

    :deep(.v-skeleton-loader) {
      background-color: colors-get(gray, 50);
    }
  }
}
</style>
